import { useCallback } from 'react';

import {
  CHART_FIELD,
  CHART_SOURCE_SEPERATOR,
} from '@/entities/chart/model/constants';
import { useFeedbackTerminology } from '@/entities/workspace';
import {
  type COMPANY_ACTIVE_STATUS,
  COMPANY_STATUS_MAP,
} from '@/features/Account/types';
import useProductStore from '@/features/Product/store/ProductStore';
import {
  useFeedbackLanguageList,
  useFeedbackRegionList,
} from '@/stores/LocaleStore';
import useTagStore from '@/stores/TagStore';
import { type CATEGORY_TYPE, CategoryDic } from '@/types/categoryConst';
import {
  type AD_PREDICTION,
  adPredictionList,
  type AGE_GROUP,
  ageGroupList,
  type FeedbackSentiment,
  type GENDER,
  genderList,
  getFeedbackSourceName,
  sentimentMap,
  type SOURCE_TYPE,
} from '@/types/feedbackConst';
import { getTagPath } from '@/utils/tag';

const useChartLabel = (meta?: ChartFieldItemMeta[]) => {
  const { replaceFeedbackTerminology } = useFeedbackTerminology();
  const tagMap = useTagStore((state) => state.tagMap);
  const productMap = useProductStore((state) => state.productMap);
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const productOptionMap = useProductStore((state) => state.productOptionMap);
  const regionList = useFeedbackRegionList();
  const languageList = useFeedbackLanguageList();

  const getLabel = useCallback(
    (
      field: CHART_FIELD,
      segment: StringNumberValue,
      exceptPath?: boolean,
    ): string => {
      switch (field) {
        case CHART_FIELD.FEEDBACK_ALL:
          return replaceFeedbackTerminology('All feedback');
        case CHART_FIELD.FEEDBACK_SOURCE: {
          const metaName = meta?.find((item) => {
            const splited = item.source?.split(CHART_SOURCE_SEPERATOR);
            const splitedSegment = segment
              .toString()
              .split(CHART_SOURCE_SEPERATOR);
            if (!splited) return false;
            return (
              splited[splited.length - 1] ===
              splitedSegment[splitedSegment.length - 1]
            );
          })?.name;

          if (!metaName) {
            const value = Number(segment) as SOURCE_TYPE;
            if (isNaN(value)) return segment.toString();
            return getFeedbackSourceName(value);
          }

          return metaName || segment.toString();
        }
        case CHART_FIELD.FEEDBACK_SENTIMENT:
        case CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL:
          return sentimentMap[segment as FeedbackSentiment]?.value;
        case CHART_FIELD.FEEDBACK_CATEGORY:
          return CategoryDic[segment as CATEGORY_TYPE] || '';
        case CHART_FIELD.FEEDBACK_TAG:
          if (!exceptPath) {
            return getTagPath(tagMap, segment as string)
              .map((tag) => tag.name)
              .join(' / ');
          }
          return tagMap[segment as string]?.name || '';
        case CHART_FIELD.ACCOUNT_ACTIVE_STATUS:
          return COMPANY_STATUS_MAP.get(segment as COMPANY_ACTIVE_STATUS) || '';
        case CHART_FIELD.TAXONOMY_TOPIC: {
          const flattenMeta = meta?.reduce((acc, item) => {
            acc.push(item);
            if (item.parent_topic_lv1) {
              acc.push({
                alias: item.parent_topic_lv1.parent_id,
                name: item.parent_topic_lv1.parent_name,
                level: 1,
              });
            }
            if (item.parent_topic_lv2) {
              acc.push({
                alias: item.parent_topic_lv2.parent_id,
                name: item.parent_topic_lv2.parent_name,
                level: 2,
              });
            }
            if (item.parent_topic_lv3) {
              acc.push({
                alias: item.parent_topic_lv3.parent_id,
                name: item.parent_topic_lv3.parent_name,
                level: 3,
              });
            }
            return acc;
          }, [] as ChartFieldItemMeta[]);
          const metaName = flattenMeta?.find(
            (item) => item.alias === segment,
          )?.name;
          return metaName || segment.toString();
        }
        case CHART_FIELD.PRODUCT_BRAND:
          return brandMap.get(segment as string)?.name || '';
        case CHART_FIELD.PRODUCT_LINE:
          return lineMap.get(segment as string)?.name || '';
        case CHART_FIELD.PRODUCT_PRODUCT:
          return productMap.get(segment as string)?.product_name || '';
        case CHART_FIELD.PRODUCT_PRODUCT_OPTION:
          return productOptionMap.get(segment as string)?.option_name || '';
        case CHART_FIELD.DEMOGRAPHIC_GENDER:
          return genderList.get(segment as GENDER) || '';
        case CHART_FIELD.DEMOGRAPHIC_AGE:
          return ageGroupList.get(segment as AGE_GROUP) || '';
        case CHART_FIELD.DEMOGRAPHIC_RACE:
          return (segment as string) || '';
        case CHART_FIELD.DEMOGRAPHIC_LOCATION:
          return (
            regionList.find((item) => item.code === segment)?.country || ''
          );
        case CHART_FIELD.FEEDBACK_LANGUAGE:
          return languageList.find((item) => item.code === segment)?.name || '';
        case CHART_FIELD.FEEDBACK_AD_PREDICTION:
          return adPredictionList.get(segment as AD_PREDICTION) || '';
        case CHART_FIELD.QUERY:
          return segment.toString();
        default: {
          const metaName = meta?.find((item) => item.alias === segment)?.name;
          return metaName || segment.toString();
        }
      }
    },
    [
      tagMap,
      brandMap,
      lineMap,
      productMap,
      productOptionMap,
      regionList,
      languageList,
      meta,
      replaceFeedbackTerminology,
    ],
  );

  return getLabel;
};

export default useChartLabel;
